import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import {
  CheckCircle,
  PhoneIphone,
  FacebookOutlined,
} from "@mui/icons-material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { checkExistingUser, requestOTP, verifyOTP } from "../../api/auth";
import { getCartCount } from "../../api/cart.api";
import { useDispatch, useSelector } from "react-redux";
import store from "../../store";
import AmericaFlag from "../../assets/icons/US-FLAG1.svg";
import IndiaFlag from "../../assets/icons/Indian-Flag.svg";
import "./login-page.scss";
import { handleLogin } from "../../common/routines/user-operations";
import { useSetLoading } from "../../common/hooks/appearance";
import ToastMessage from "../../common/components/toast-message/toast-message";
import { AlertComponent } from "../../common/components";

interface ActivationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
}

const ActivationCodeInput: React.FC<ActivationCodeInputProps> = ({
  value,
  onChange,
}) => {
  const handleChange = (index: number, inputValue: string) => {
    if (inputValue.length <= 1 && /^\d*$/.test(inputValue)) {
      const newValue = value.split("");
      newValue[index] = inputValue;
      onChange(newValue.join(""));

      // Auto-focus next input
      if (inputValue && index < 5) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  return (
    <Box className="activation-code-inputs">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <TextField
          key={index}
          id={`code-input-${index}`}
          value={value[index] || ""}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          type="text"
          autoComplete="off"
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "bold",
            },
          }}
          sx={{
            "& .MuiOutlinedInput-input": {
              fontSize: { xs: "16px", sm: "18px" },
              padding: { xs: "12px 4px !important", sm: "16px 8px !important" },
            },
          }}
          className="code-input"
          variant="outlined"
        />
      ))}
    </Box>
  );
};

const ProviderRegistration: React.FC = () => {
  const [step, setStep] = useState<"login" | "activation">("login");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [activationCode, setActivationCode] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [resendTimer, setResendTimer] = useState(30);
  const [isPhoneNumberLogin, setIsPhoneNumberLogin] = useState<boolean>(true);
  const [continueDisabled, setContinueDisabled] = useState<boolean>(true);
  const [otpError, setOtpError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);

  const params = new URLSearchParams(location.search);
  const redirectFromQuery = params.get("redirect");
  const redirectFromStorage = localStorage.getItem("postLoginRedirect");
  const redirect = redirectFromQuery || redirectFromStorage || "/home";

  useEffect(() => {
    if (redirectFromQuery && !localStorage.getItem("postLoginRedirect")) {
      localStorage.setItem("postLoginRedirect", redirectFromQuery);
    }
  }, [redirectFromQuery]);

  const appState = store.getState();
  const [showToast, setShowToast] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
    close: () => void;
  }>({ open: false, message: "", severity: "info", close: () => {} });

  const ServerURL = `${appState.server.serverProtocol}://${appState.server.serverUrl}:${appState.server.serverPort}/api`;
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [isFuneralDirector, setIsFuneralDirector] = useState<boolean | null>(
    null,
  );
  const [isExistingUser, setIsExistingUser] = useState<boolean | null>(null);
  const [selectedRole, setSelectedRole] = useState<boolean | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const isLoading = useSelector((state: any) => state.appearance.isLoading);

  const showLoader = () => {
    store.dispatch({
      type: `APPEARANCE:SET_LOADING`,
      payload: { value: true },
    });
  };
  const hideLoader = () => {
    store.dispatch({
      type: `APPEARANCE:SET_LOADING`,
      payload: { value: false },
    });
  };
  const handleContinue = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isLoading) return;

    showLoader();

    try {
      let payload: any;

      if (isPhoneNumberLogin && phoneNumber) {
        payload = {
          type: "mobile",
          mobileNumber: countryCode + phoneNumber,
        };
      } else if (!isPhoneNumberLogin && email) {
        payload = {
          type: "email",
          email,
        };
      } else {
        hideLoader()
      }
      // STEP 1 → Check existing user
      const checkRes = await checkExistingUser(payload);

      // 🚨 IMPORTANT: Stop if API returns success false
      if (!checkRes?.data?.success) {
        setAlertType("error");
        setAlertMessage(checkRes?.data?.message || "Unable to verify user");
        setAlertOpen(true);
        hideLoader()
        return; // 🔥 STOP HERE
      }

      const exists = checkRes?.data?.data?.userExists;
      setIsExistingUser(exists);

      if (!exists) {
        setShowRoleSelection(true);
        hideLoader()
        return;
      }

      // STEP 2 → Request OTP
      const otpRes = await requestOTP(payload);

      if (otpRes?.data?.success) {
        setStep("activation");
        hideLoader()
      } else {
        setAlertType("error");
        setAlertMessage(otpRes?.data?.message || "Failed to send OTP");
        setAlertOpen(true);
        hideLoader()
      }
    } catch (error: any) {
      console.error(error);
      if (
        !error?.response?.data?.success &&
        error?.response?.data?.errorCode === "USER_ACCOUNT_INACTIVE"
      ) {
        const message: any =
          error?.response?.data?.message || "The user account is inactive.";
        setShowToast({
          open: true,
          message,
          severity: "error",
          close: () => {},
        });
      }
    } finally {
       hideLoader()
    }
  };

  const handleSubmitCode = async () => {
    showLoader()
    setOtpError(null);
    try {
      let payload = {};

      if (
        isPhoneNumberLogin &&
        !phoneNumberError &&
        phoneNumber &&
        countryCode
      ) {
        payload = {
          type: "mobile",
          mobileNumber: countryCode + phoneNumber,
          otp: activationCode,
        };
      } else if (!isPhoneNumberLogin && !emailError && email) {
        payload = {
          type: "email",
          email,
          otp: activationCode,
        };
      }

      const response = await verifyOTP(payload);
      hideLoader();
      // success case
      setActivationCode("");
     if (response?.data?.success === true && response?.data) {
        const userData = response?.data?.data;
        handleLogin(userData);

        const postLoginRedirect = localStorage.getItem("postLoginRedirect");
        const finalRedirect =
          postLoginRedirect ||
          redirectFromQuery ||
          "/home";

        navigate(finalRedirect, { replace: true });
        if (postLoginRedirect) {
          localStorage.removeItem("postLoginRedirect");
        }
      }
    } catch (error: any) {
      setActivationCode("");

      const response = error?.response;
      const data = response?.data;
      const code = data?.errorCode;
      const msg = data?.message;

      if (code === "INVALID_OTP") {
        setOtpError("Invalid OTP entered. Please try again.");
      } else if (code === "OTP_EXPIRED") {
        setOtpError("OTP expired. Request a new code.");
      } else if (code === "TOO_MANY_REQUESTS") {
        setOtpError("Too many requests. Please retry after some time.");
      } else {
        // any other msg
        setOtpError(msg || "Something went wrong. Please try again.");
      }
      handleResendCode();
    } finally {
      hideLoader();
    }
  };

  const handleResendCode = () => {
    if (attemptsLeft > 0) {
      setAttemptsLeft((prev) => prev - 1);
      setResendTimer(30);

      if (email) {
        requestOTP({
          type: "email",
          email,
        });
      } else {
        requestOTP({
          type: "mobile",
          mobileNumber: countryCode + phoneNumber,
        });
      }
    }
  };

  const handlelLoginOption = () => {
    setIsPhoneNumberLogin(!isPhoneNumberLogin);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneNumberChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value) || value.length > 10) return;

    setPhoneNumber(value);
    if (validatePhoneNumber(value)) {
      setPhoneNumberError("");
      setContinueDisabled(false);
    } else {
      setPhoneNumberError("Phone number must be 10 digits");
      setContinueDisabled(true);
    }
  };

  const handlePhoneNumberBlur = () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneNumberError("Phone number must be 10 digits");
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (validateEmail(value)) {
      setEmailError("");
      setContinueDisabled(false);
    } else {
      setEmailError("Invalid email format");
      setContinueDisabled(true);
    }
  };

  const handleEmailBlur = () => {
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };
  const handleRoleSelection = async (roleValue: boolean) => {
    if (isLoading) return;
    try {
      showLoader();
      setIsFuneralDirector(roleValue); // ✅ just store locally

      let payload: any;

      if (isPhoneNumberLogin) {
        payload = {
          type: "mobile",
          mobileNumber: countryCode + phoneNumber,
          isFuneralDirector: roleValue,
        };
      } else {
        payload = {
          type: "email",
          email,
          isFuneralDirector: roleValue,
        };
      }

      const response = await requestOTP(payload);

      if (response.status === 200) {
        setShowRoleSelection(false);
        setStep("activation");
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("userDetails");

    if (!storedUser) return;

    fetchCartCount();
  }, []);

  useEffect(() => {
    if (step !== "activation") return;

    if (resendTimer === 0) return;

    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step, resendTimer]);

  useEffect(() => {
    return () => {
      hideLoader();
    };
  }, []);

  const fetchCartCount = async () => {
    if (!isLoggedIn) return;
    try {
      const response = await getCartCount();
      const count = response.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count: count } });
    } catch (error) {
      console.error("ERROR IN FETCHING CART COUNT::", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, [isLoggedIn]);

  useEffect(() => {
    if (step === "activation" && attemptsLeft <= 0 && resendTimer === 0) {
      alert("OTP attempts exhausted. Please login again.");

      // reset everything & go back to login
      setStep("login");
      setActivationCode("");
      setOtpError(null);

      setAttemptsLeft(3);
      setResendTimer(30);
      hideLoader();
      setContinueDisabled(false);

      // clear phone/email input
      setPhoneNumber("");
      setEmail("");
    }
  }, [step, attemptsLeft, resendTimer]);

  useEffect(() => {
    if (step === "activation") {
      setActivationCode("");
      setOtpError(null);

      // optional: focus first box
      setTimeout(() => {
        const first = document.getElementById(
          "code-input-0",
        ) as HTMLInputElement | null;
        first?.focus();
      }, 0);
    }
  }, [step]);

  return (
    <div className="provider-registration">
      <Container maxWidth={false} className="registration-container">
        <Box className="registration-content">
          {/* Left Side Content */}
          <Box className="left-content">
            <Typography variant="h4" className="welcome-title">
              Welcome to MemorialGo User Registration
            </Typography>

            <Box className="image-container">
              <img
                src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=280&fit=crop"
                alt="Hands holding together"
                className="hero-image"
              />
            </Box>

            <Typography variant="h5" className="memorialgo-title">
              MemorialGo Your Partner in After-Life Care
            </Typography>

            <Typography variant="body1" className="description">
              We empower you to prepare for life&apos;s inevitable transitions
              with care and compassion.
            </Typography>

            <Typography variant="h6" className="access-title">
              Login to access
            </Typography>

            <List className="features-list">
              <ListItem className="feature-item">
                <ListItemIcon>
                  <CheckCircle className="check-icon" />
                </ListItemIcon>
                <ListItemText
                  primary="Personalized planning tools for after-life care."
                  className="feature-text"
                />
              </ListItem>
              <ListItem className="feature-item">
                <ListItemIcon>
                  <CheckCircle className="check-icon" />
                </ListItemIcon>
                <ListItemText
                  primary="Support services for your family and loved ones"
                  className="feature-text"
                />
              </ListItem>
              <ListItem className="feature-item">
                <ListItemIcon>
                  <CheckCircle className="check-icon" />
                </ListItemIcon>
                <ListItemText
                  primary="Updates and management of your care plan, all in one place."
                  className="feature-text"
                />
              </ListItem>
            </List>
          </Box>

          {/* Right Side Forms */}
          <Box className="right-content">
            {step === "login" ? (
              <Box className="login-form">
                <Typography variant="h5" className="form-title">
                  Log in to MemorialGo
                </Typography>

                <Typography variant="body2" className="mobile-label">
                  {isPhoneNumberLogin ? "Mobile Number" : "Email Address"}
                </Typography>
                {isPhoneNumberLogin === true ? (
                  <Box className="phone-input-container">
                    <FormControl className="country-select" variant="outlined">
                      <Select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        renderValue={(value) => (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img
                              src={value === "+1" ? AmericaFlag : IndiaFlag}
                              alt="flag"
                              style={{ width: 24, height: 16, marginRight: 8 }}
                            />
                            {value}
                          </Box>
                        )}
                      >
                        <MenuItem value="+1">
                          <img
                            src={AmericaFlag}
                            style={{ width: 24, marginRight: 8 }}
                          />
                          +1
                        </MenuItem>

                        <MenuItem value="+91">
                          <img
                            src={IndiaFlag}
                            style={{ width: 24, marginRight: 8 }}
                          />
                          +91
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      placeholder="1234567890"
                      variant="outlined"
                      className="phone-input"
                      fullWidth
                      onBlur={handlePhoneNumberBlur}
                      error={!!phoneNumberError}
                      helperText={phoneNumberError}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Box>
                ) : (
                  <Box className="email-input-container">
                    <TextField
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="example@abc.com"
                      variant="outlined"
                      className="email-input"
                      fullWidth
                      onBlur={handleEmailBlur}
                      error={!!emailError}
                      helperText={emailError}
                    />
                  </Box>
                )}
                {showRoleSelection && (
                  <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 0.5,
                        fontWeight: "400 !important",
                        fontSize: "14px",
                        color: "#666666",
                      }}
                    >
                      Tell us how you want to use Memorial Go
                    </Typography>

                    <RadioGroup
                      row
                      value={selectedRole}
                      onChange={(e) => {
                        const roleValue = e.target.value === "true";
                        setSelectedRole(roleValue); // update UI
                        handleRoleSelection(roleValue);
                      }}
                      sx={{
                        gap: { xs: 1, sm: 3 },
                        mb: 2,
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" />}
                        label="Buying for Self/Family"
                        sx={{
                          margin: 0,
                          "& .MuiTypography-root": {
                            color: "#666666",
                            fontSize: "14px",
                            fontWeight: "400 !important",
                          },
                        }}
                      />

                      <FormControlLabel
                        value={true}
                        control={<Radio size="small" />}
                        label="I am a Funeral Director"
                        sx={{
                          margin: 0,
                          "& .MuiTypography-root": {
                            color: "#666666",
                            fontSize: "14px",
                            fontWeight: "400 !important",
                          },
                        }}
                      />
                    </RadioGroup>
                  </Box>
                )}

                {!showRoleSelection && (
                  <Button
                    type="button"
                    variant="contained"
                    fullWidth
                    className="continue-button"
                    onClick={handleContinue}
                    disabled={continueDisabled || isLoading}
                  >
                    Continue
                  </Button>
                )}
                <Divider className="or-divider-wrapper">
                  {" "}
                  <Typography variant="body2" className="or-text">
                    Or Login/ Signup with
                  </Typography>
                </Divider>

                <Box className="social-buttons">
                  {isPhoneNumberLogin === true ? (
                    <Button
                      variant="outlined"
                      className="social-button"
                      startIcon={
                        <EmailOutlinedIcon
                          sx={{ stroke: "currentColor", strokeWidth: 0.5 }}
                        />
                      }
                      onClick={handlelLoginOption}
                    >
                      Mail
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      className="social-button"
                      startIcon={<PhoneIphone />}
                      onClick={handlelLoginOption}
                    >
                      Mobile
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    className="social-button"
                    startIcon={
                      <img
                        src="https://developers.google.com/identity/images/g-logo.png"
                        width={15}
                      />
                    }
                    onClick={() =>
                      (window.location.href = `${ServerURL}/auth/google?state=${encodeURIComponent(
                        JSON.stringify({ redirect }),
                      )}`)
                    }
                  >
                    Google
                  </Button>
                  <Button
                    variant="outlined"
                    className="social-button"
                    startIcon={<FacebookOutlined sx={{ color: "#1877F2" }} />}
                    onClick={() =>
                      (window.location.href = `${ServerURL}/auth/facebook?state=${encodeURIComponent(
                        JSON.stringify({ redirect }),
                      )}`)
                    }
                  >
                    Facebook
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box className="activation-form">
                <Typography variant="h5" className="form-title">
                  Activate Your Account!
                </Typography>

                <Typography variant="body2" className="activation-description">
                  We have sent a 6-digit code to the email and phone number you
                  have provided. Please enter the code to activate your account.
                </Typography>

                <ActivationCodeInput
                  value={activationCode}
                  onChange={setActivationCode}
                />
                {/* Error Message for Invalid OTP */}
                {otpError && (
                  <Typography
                    variant="body2"
                    color="error"
                    className="error-message"
                  >
                    {otpError}
                  </Typography>
                )}

                <Typography variant="body2" className="resend-text">
                  Didn&apos;t get a code?
                </Typography>

                <Box className="resend-container">
                  <Link
                    component="button"
                    variant="body2"
                    className={`resend-link ${resendTimer > 0 || attemptsLeft <= 0 ? "disabled" : ""}`}
                    onClick={handleResendCode}
                  >
                    Resend Code
                  </Link>
                  <Typography variant="body2" className="timer-text">
                    0:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}
                  </Typography>
                  <Typography variant="body2" className="attempts-text">
                    ({attemptsLeft} attempts left)
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  className="submit-button"
                  onClick={handleSubmitCode}
                  disabled={activationCode.length !== 6 || attemptsLeft <= 0}
                >
                  Submit
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
      {showToast.open && (
        <ToastMessage
          open={showToast.open}
          message={showToast.message}
          severity={showToast.severity}
          onClose={() => {
            setShowToast({ ...showToast, open: false });
          }}
        />
      )}
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ProviderRegistration;
